import {
  createContext,
  FC,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  useGetCustomWidgetListQuery,
  useGetDashboardsListQuery,
  useUpdateDashboardMutation,
} from "../../../api/network/overviewApiServices";
import { ListingResponse } from "../../../api/types/Listing";
import { CustomWidget, DashboardItem } from "../../../api/types/Overview";
import { REPORT_DS_TIME_RANGE } from "../../../api/types/Report";
import {
  APP_CONFIG,
  REPORT_FILTER_BY_OPTION_VALUES,
} from "../../../constants/constants";
import { getAccountTimezoneNoHook } from "../../../hooks/timezone";
import {
  dbTimeToJsTime,
  formattedDateTimeBasedOnTimezone,
  getStartEndOfDayInUTC,
  jsTimeToDBTime,
} from "../../../utils/dateTime";
import { localStorageKeys } from "../../../utils/localStorageItems";
import { TDashboardColumns } from "../types/overview.types";

const dashboardColumnsDefaultValue: TDashboardColumns = {
  col1: {
    id: "Col 1",
    colWidgets: [],
  },
  col2: {
    id: "Col 2",
    colWidgets: [],
  },
  col3: {
    id: "Col 3",
    colWidgets: [],
  },
};

type TDashboardTimeFilter = {
  slug: string;
  title: string;
  timeRange: [number, number];
  value: REPORT_DS_TIME_RANGE;
};

const accountTzNoHook = getAccountTimezoneNoHook();
const refDate = new Date();
if (APP_CONFIG.DEBUG.ALL) {
  console.log("== accountTzNoHook", accountTzNoHook);
  console.error(
    "== refDate",
    jsTimeToDBTime(refDate.getTime()),
    formattedDateTimeBasedOnTimezone(
      refDate.getTime(),
      accountTzNoHook,
      "MM/dd/yyyy (hh:mmaa)"
    )
  );
}
const tempDashFiltersDate = localStorage.getItem(
  localStorageKeys.TEMP_DASH_FILTERS_DATE
);
if (!!tempDashFiltersDate) {
  // enable this block for testing using data of April / May 2024 - or a specific date
  const dateChunks = tempDashFiltersDate.split("-");
  if (dateChunks.length === 3) {
    refDate.setFullYear(parseInt(dateChunks[0]));
    refDate.setMonth(parseInt(dateChunks[1]) - 1);
    refDate.setDate(parseInt(dateChunks[2]));
  }
}
// refDate.setDate(refDate.getDate() - 1); // yesterday
const yesterdaysDate = new Date(
  refDate.getFullYear(),
  refDate.getMonth(),
  refDate.getDate() - 1
);
const timeFilters = [
  {
    slug: "current-month",
    title: "Current Month",
    timeRange: [
      // new Date(refDate.getFullYear(), refDate.getMonth(), 1),
      jsTimeToDBTime(
        getStartEndOfDayInUTC(
          formattedDateTimeBasedOnTimezone(
            new Date(refDate.getFullYear(), refDate.getMonth(), 2).getTime(),
            accountTzNoHook,
            "yyyy-MM-dd"
          ),
          accountTzNoHook
        ).startOfDayInUTC.getTime()
      ),
      // yesterdaysDate,
      jsTimeToDBTime(
        getStartEndOfDayInUTC(
          formattedDateTimeBasedOnTimezone(
            new Date(
              refDate.getFullYear(),
              refDate.getMonth(),
              refDate.getDate()
            ).getTime(),
            accountTzNoHook,
            "yyyy-MM-dd"
          ),
          accountTzNoHook
        ).endOfDayInUTC.getTime()
      ),
    ] as [number, number],
    value: REPORT_DS_TIME_RANGE.CURRENT_MONTH,
  },
  {
    slug: "current-previous-month",
    title: "Current & Previous Month",
    timeRange: [
      // new Date(refDate.getFullYear(), refDate.getMonth() - 1, 1),
      jsTimeToDBTime(
        getStartEndOfDayInUTC(
          formattedDateTimeBasedOnTimezone(
            new Date(
              refDate.getFullYear(),
              refDate.getMonth() - 1,
              2
            ).getTime(),
            accountTzNoHook,
            "yyyy-MM-dd"
          ),
          accountTzNoHook
        ).startOfDayInUTC.getTime()
      ),
      // yesterdaysDate,
      jsTimeToDBTime(
        getStartEndOfDayInUTC(
          formattedDateTimeBasedOnTimezone(
            new Date(
              refDate.getFullYear(),
              refDate.getMonth(),
              refDate.getDate()
            ).getTime(),
            accountTzNoHook,
            "yyyy-MM-dd"
          ),
          accountTzNoHook
        ).endOfDayInUTC.getTime()
      ),
    ] as [number, number],
    value: REPORT_DS_TIME_RANGE.CURRENT_AND_PREVIOUS_MONTH,
  },
  {
    slug: "current-previous-two-months",
    title: "Current & Previous Two Months",
    timeRange: [
      // new Date(refDate.getFullYear(), refDate.getMonth() - 2, 1),
      jsTimeToDBTime(
        getStartEndOfDayInUTC(
          formattedDateTimeBasedOnTimezone(
            new Date(
              refDate.getFullYear(),
              refDate.getMonth() - 2,
              2
            ).getTime(),
            accountTzNoHook,
            "yyyy-MM-dd"
          ),
          accountTzNoHook
        ).startOfDayInUTC.getTime()
      ),
      // yesterdaysDate,
      jsTimeToDBTime(
        getStartEndOfDayInUTC(
          formattedDateTimeBasedOnTimezone(
            new Date(
              refDate.getFullYear(),
              refDate.getMonth(),
              refDate.getDate()
            ).getTime(),
            accountTzNoHook,
            "yyyy-MM-dd"
          ),
          accountTzNoHook
        ).endOfDayInUTC.getTime()
      ),
    ] as [number, number],
    value: REPORT_DS_TIME_RANGE.CURRENT_AND_PREVIOUS_TWO_MONTHS,
  },
  {
    slug: "previous-quarter",
    title: "Last Three Months",
    timeRange: [
      // new Date(refDate.getFullYear(), refDate.getMonth() - 3, 1),
      jsTimeToDBTime(
        getStartEndOfDayInUTC(
          formattedDateTimeBasedOnTimezone(
            new Date(
              refDate.getFullYear(),
              refDate.getMonth() - 3,
              2
            ).getTime(),
            accountTzNoHook,
            "yyyy-MM-dd"
          ),
          accountTzNoHook
        ).startOfDayInUTC.getTime()
      ),
      // new Date(refDate.getFullYear(), refDate.getMonth(), 0),
      jsTimeToDBTime(
        getStartEndOfDayInUTC(
          formattedDateTimeBasedOnTimezone(
            new Date(refDate.getFullYear(), refDate.getMonth(), 1).getTime(),
            accountTzNoHook,
            "yyyy-MM-dd"
          ),
          accountTzNoHook
        ).endOfDayInUTC.getTime()
      ),
    ],
    value: REPORT_DS_TIME_RANGE.PREVIOUS_QUARTER,
  },
] satisfies TDashboardTimeFilter[] as TDashboardTimeFilter[];

if (APP_CONFIG.DEBUG.ALL) {
  console.log("== timeFilters");
  timeFilters.forEach((item, i) => {
    console.log("== ==", item.slug, item.timeRange[0], item.timeRange[1]);
    console.log(
      "== ==",
      item.slug,
      formattedDateTimeBasedOnTimezone(
        dbTimeToJsTime(item.timeRange[0]),
        accountTzNoHook,
        "MM/dd/yyyy (hh:mmaa)"
      ),
      formattedDateTimeBasedOnTimezone(
        dbTimeToJsTime(item.timeRange[1]),
        accountTzNoHook,
        "MM/dd/yyyy (hh:mmaa)"
      )
    );
  });
}

const contextDefaultValue = {
  dashboardList: {
    results: [],
    count: 0,
    next: "",
    previous: "",
  } as ListingResponse<DashboardItem[]> | undefined,
  isFetchingDashboardList: false,
  refetchDashboardList: () => {},
  isUninitializedDashboardList: true,
  dashList: undefined as DashboardItem[] | undefined,
  setDashList: (dashList: DashboardItem[]) => {},
  selectedDashboard: null as number | null,
  setSelectedDashboard: (dashboardItem: any) => {},
  dashboardColumns: dashboardColumnsDefaultValue,
  setDashboardColumns: (dashboardColumns: any) => {},
  allTimeFilters: timeFilters,
  timeFilter: timeFilters[0].slug,
  setTimeFilter: (timeFilter: any) => {},
  allGroupsSelected: false,
  setAllGroupsSelected: (allGroupsSelected: any) => {},
  selectedGroups: [] as string[],
  allAssetsSelected: false,
  allCamerasSelected: false,
  setAllAssetsSelected: (allAssetsSelected: any) => {},
  setAllCamerasSelected: (allCamerasSelected: any) => {},
  groupSearchKeyword: "",
  setGroupSearchKeyword: (groupSearchKeyword: string) => {},
  assetSearchKeyword: "",
  cameraSearchKeyword: "",
  setAssetSearchKeyword: (assetSearchKeyword: string) => {},
  setCameraSearchKeyword: (cameraSearchKeyword: string) => {},
  selectedAssets: [] as string[],
  selectedCameras: [] as string[],
  selectedDataType: REPORT_FILTER_BY_OPTION_VALUES.GROUP,
  setSelectedDataType: (selectedDataType: any) => {},
  setSelectedGroups: (selectedGroups: string[]) => {},
  setSelectedAssets: (selectedAssets: string[]) => {},
  setSelectedCameras: (selectedCameras: string[]) => {},
  onGroupSelectionConfirm: null as any,
  isLoadingUpdateDashboard: false,
  customListRespone: {
    results: [],
    count: 0,
    next: "",
    previous: "",
  } as ListingResponse<CustomWidget[]> | undefined,
  isFetchingCustomList: false,
  refetchCustomWidgetList: () => {},
  setCustomList: (customList: CustomWidget[]) => {},
  customList: undefined as CustomWidget[] | undefined,
  widgetMarkedForDeletion: null as CustomWidget | null,
  setWidgetMarkedForDeletion: (widget: CustomWidget | null) => {},
};

const overviewContext = createContext(contextDefaultValue);

export const OverviewContextProvider: FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "overviewScreen" });

  const [dashList, setDashList] = useState(contextDefaultValue.dashList);
  const [customList, setCustomList] = useState(contextDefaultValue.customList);
  const [selectedDashboard, setSelectedDashboard] = useState<number | null>(
    contextDefaultValue.selectedDashboard
  );
  const [dashboardColumns, setDashboardColumns] = useState(
    contextDefaultValue.dashboardColumns
  );
  const [timeFilter, setTimeFilter] = useState<string>(timeFilters[0].slug);
  const [allTimeFilters] = useState(timeFilters);
  const [allGroupsSelected, setAllGroupsSelected] = useState(true);
  const [allAssetsSelected, setAllAssetsSelected] = useState(true);
  const [allCamerasSelected, setAllCamerasSelected] = useState(true);
  const [groupSearchKeyword, setGroupSearchKeyword] = useState("");
  const [assetSearchKeyword, setAssetSearchKeyword] = useState("");
  const [cameraSearchKeyword, setCameraSearchKeyword] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [selectedCameras, setSelectedCameras] = useState<string[]>([]);
  const [selectedDataType, setSelectedDataType] = useState(
    REPORT_FILTER_BY_OPTION_VALUES.GROUP
  );
  const onGroupSelectionConfirm = useRef((values: any) => {});

  const [widgetMarkedForDeletion, setWidgetMarkedForDeletion] =
    useState<CustomWidget | null>(null);

  const {
    data: dashboardList,
    isFetching: isFetchingDashboardList,
    refetch: refetchDashboardList,
    isUninitialized: isUninitializedDashboardList,
  } = useGetDashboardsListQuery({
    page: 1,
    page_size: APP_CONFIG.LISTINGS.LARGE_PAGE_SIZE,
  });

  const {
    data: customListRespone,
    isFetching: isFetchingCustomList,
    refetch: refetchCustomWidgetList,
  } = useGetCustomWidgetListQuery();

  useEffect(() => {
    // Set default selected dashboard if not already set
    if (
      !isFetchingDashboardList &&
      !!dashboardList?.results?.length &&
      !selectedDashboard
    ) {
      setSelectedDashboard(
        dashboardList.results.find((d) => d.is_default)?.id ||
          dashboardList.results[0].id
      );
    }
    if (
      !!dashboardList &&
      !isFetchingDashboardList
      // && typeof dashList === 'undefined'
    ) {
      setDashList(dashboardList.results);
    }
  }, [dashboardList, isFetchingDashboardList]);

  useEffect(() => {
    if (!!customListRespone && !isFetchingCustomList) {
      setCustomList(customListRespone.results);
    }
  }, [customListRespone, isFetchingCustomList]);

  // on selection of dashboard, update the column widgets
  useEffect(() => {
    if (!selectedDashboard) {
      setDashboardColumns(dashboardColumnsDefaultValue);
      return;
    }
    const selectedDashboardItem = dashboardList?.results?.find(
      (d) => d.id === selectedDashboard
    );
    if (!selectedDashboardItem) {
      setDashboardColumns(dashboardColumnsDefaultValue);
      return;
    }
    const newDashboardColumns = JSON.parse(
      JSON.stringify(dashboardColumnsDefaultValue)
    );
    selectedDashboardItem.widgets.forEach((widget) => {
      if (widget.page_column === 1) {
        newDashboardColumns.col1.colWidgets.push(widget);
      } else if (widget.page_column === 2) {
        newDashboardColumns.col2.colWidgets.push(widget);
      } else if (widget.page_column === 3) {
        newDashboardColumns.col3.colWidgets.push(widget);
      }
    });
    setDashboardColumns(newDashboardColumns);
  }, [selectedDashboard, dashboardList]);

  // save moved widgets
  // const [saveMovedDashboardWidgetAPITrigger] = useSaveMovedDashboardWidgetMutation();
  const [updateDashboardAPITrigger, { isLoading: isLoadingUpdateDashboard }] =
    useUpdateDashboardMutation();
  useEffect(() => {
    if (!selectedDashboard) return;

    // prevent API call if dashboard has changed
    if (
      dashboardList?.results?.find((d) => d.id === selectedDashboard)?.widgets
        .length !==
      dashboardColumns.col1.colWidgets.length +
        dashboardColumns.col2.colWidgets.length +
        dashboardColumns.col3.colWidgets.length
    )
      return;

    // identify if widget is moved by checking aggregate widgets count in dashboardColumns
    if (
      dashboardColumns.col1.colWidgets.length ===
        dashboardList?.results
          ?.find((d) => d.id === selectedDashboard)
          ?.widgets?.filter((w) => w.page_column === 1).length &&
      dashboardColumns.col2.colWidgets.length ===
        dashboardList?.results
          ?.find((d) => d.id === selectedDashboard)
          ?.widgets?.filter((w) => w.page_column === 2).length &&
      dashboardColumns.col3.colWidgets.length ===
        dashboardList?.results
          ?.find((d) => d.id === selectedDashboard)
          ?.widgets?.filter((w) => w.page_column === 3).length
    )
      return;

    // save moved widgets API call
    updateDashboardAPITrigger({
      // saveMovedDashboardWidgetAPITrigger({
      id: selectedDashboard,
      widgets: [
        ...(dashboardColumns.col1.colWidgets?.map((widget, index) => ({
          ...widget,
          page_column: 1,
        })) || []),
        ...(dashboardColumns.col2.colWidgets?.map((widget, index) => ({
          ...widget,
          page_column: 2,
        })) || []),
        ...(dashboardColumns.col3.colWidgets?.map((widget, index) => ({
          ...widget,
          page_column: 3,
        })) || []),
      ],
    })
      .unwrap()
      .then((data) => {
        toast.success(t("toast.move_success"), {
          autoClose: 2000,
          position: "top-center",
        });
        // refetch dashboard list
        refetchDashboardList();
      })
      .catch((error) => {
        toast.error(t("toast.move_failed"));
      });
  }, [dashboardColumns]);

  return (
    <overviewContext.Provider
      value={{
        dashboardList,
        isFetchingDashboardList,
        refetchDashboardList,
        isUninitializedDashboardList,
        dashList,
        setDashList,
        selectedDashboard,
        setSelectedDashboard,
        dashboardColumns,
        setDashboardColumns,
        allTimeFilters,
        timeFilter,
        setTimeFilter,
        allGroupsSelected,
        setAllGroupsSelected,
        selectedGroups,
        setSelectedGroups,
        setSelectedAssets,
        setSelectedCameras,
        onGroupSelectionConfirm,
        allAssetsSelected,
        allCamerasSelected,
        setAllAssetsSelected,
        setAllCamerasSelected,
        groupSearchKeyword,
        setGroupSearchKeyword,
        assetSearchKeyword,
        cameraSearchKeyword,
        setAssetSearchKeyword,
        setCameraSearchKeyword,
        selectedAssets,
        selectedCameras,
        setSelectedDataType,
        selectedDataType,
        isLoadingUpdateDashboard,
        customListRespone,
        customList,
        isFetchingCustomList,
        refetchCustomWidgetList,
        setCustomList,
        widgetMarkedForDeletion,
        setWidgetMarkedForDeletion,
      }}
    >
      {children}
    </overviewContext.Provider>
  );
};

export const useOverviewContext = () => {
  const {
    dashboardList,
    isFetchingDashboardList,
    refetchDashboardList,
    isUninitializedDashboardList,
    dashList,
    setDashList,
    selectedDashboard,
    setSelectedDashboard,
    dashboardColumns,
    setDashboardColumns,
    allTimeFilters,
    timeFilter,
    setTimeFilter,
    allGroupsSelected,
    setAllGroupsSelected,
    selectedGroups,
    allAssetsSelected,
    allCamerasSelected,
    setAllAssetsSelected,
    setAllCamerasSelected,
    groupSearchKeyword,
    setGroupSearchKeyword,
    assetSearchKeyword,
    cameraSearchKeyword,
    setAssetSearchKeyword,
    setCameraSearchKeyword,
    selectedAssets,
    selectedCameras,
    setSelectedDataType,
    selectedDataType,
    setSelectedGroups,
    setSelectedAssets,
    setSelectedCameras,
    isLoadingUpdateDashboard,
    customListRespone,
    customList,
    isFetchingCustomList,
    refetchCustomWidgetList,
    setCustomList,
    widgetMarkedForDeletion,
    setWidgetMarkedForDeletion,
  } = useContext(overviewContext);
  return {
    dashboardList,
    isFetchingDashboardList,
    refetchDashboardList,
    isUninitializedDashboardList,
    dashList,
    setDashList,
    selectedDashboard,
    setSelectedDashboard,
    dashboardColumns,
    setDashboardColumns,
    allTimeFilters,
    timeFilter,
    setTimeFilter,
    allGroupsSelected,
    setAllGroupsSelected,
    selectedGroups,
    allAssetsSelected,
    allCamerasSelected,
    setAllAssetsSelected,
    setAllCamerasSelected,
    groupSearchKeyword,
    setGroupSearchKeyword,
    assetSearchKeyword,
    cameraSearchKeyword,
    setAssetSearchKeyword,
    setCameraSearchKeyword,
    selectedAssets,
    selectedCameras,
    setSelectedDataType,
    selectedDataType,
    setSelectedGroups,
    setSelectedAssets,
    setSelectedCameras,
    isLoadingUpdateDashboard,
    customListRespone,
    customList,
    isFetchingCustomList,
    refetchCustomWidgetList,
    setCustomList,
    widgetMarkedForDeletion,
    setWidgetMarkedForDeletion,
  };
};
